import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BottomSheet } from "@/components/BottomSheet";
import { useUI } from "@/context/UIContext";
import { useLang } from "@/context/LanguageContext";
import { useOrder } from "@/context/OrderContext";
import { useSession } from "@/context/SessionContext";
import { restaurantConfig, serviceStaff } from "@/config/restaurantConfig";
import { localize } from "@/lib/format";
import { getStaffRatingStatus, staffRatingTarget, submitStaffRating } from "@/lib/ratings";

function Row({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testId}>
      <span className="text-sm font-semibold">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => onChange(n)} aria-label={`${label} ${n}`} data-testid={`${testId}-star-${n}`} className="active:scale-90">
            <Star className={`h-6 w-6 transition-colors ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

// Private staff rating, stored locally (mock). The customer never sees staff
// history, averages, rankings or other guests' reviews — only "already rated".
export function StaffRatingSheet() {
  const { staffRatingOpen, closeStaffRating } = useUI();
  const { order } = useOrder();
  const { sessionId } = useSession();
  const { lang, t } = useLang();
  const [overall, setOverall] = useState(0);
  const [care, setCare] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [courtesy, setCourtesy] = useState(0);
  const [comment, setComment] = useState("");

  // Rating target = the staff assignment of the current order (or table session).
  const staffId = order?.staffId || serviceStaff.id;
  const target = { staffId, orderId: order?.id, sessionId };
  const { alreadyRated } = staffRatingOpen ? getStaffRatingStatus(staffRatingTarget(target)) : { alreadyRated: false };
  const canSubmit = !alreadyRated && overall && care && speed && courtesy;

  const reset = () => { setOverall(0); setCare(0); setSpeed(0); setCourtesy(0); setComment(""); };

  const submit = () => {
    const result = submitStaffRating({
      ...target,
      branchId: restaurantConfig.branch.id,
      scores: { overall, care, speed, courtesy },
      comment,
    });
    // Not saved: keep the sheet + answers so the guest can retry; never "rated".
    if (result.reason === "storage") {
      toast.error(t("rating_saveFailed"));
      return;
    }
    closeStaffRating();
    reset();
    if (result.ok) toast.success(t("rw_thanks"));
    else toast(t("rw_already"));
  };

  return (
    <BottomSheet open={staffRatingOpen} onClose={closeStaffRating} maxHeight="86vh" testId="staff-rating-sheet">
      <div className="px-5 pb-6 pt-1">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-full brand-gradient font-heading text-lg font-extrabold text-white">
            {serviceStaff.initial}
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("servedBy")}</p>
            <p className="font-heading text-lg font-extrabold leading-tight">{serviceStaff.firstName}</p>
            <p className="text-xs text-muted-foreground">{localize(serviceStaff.role, lang)}</p>
          </div>
        </div>

        {alreadyRated ? (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold" data-testid="rw-already-rated">{t("rw_already")}</p>
        ) : (
          <>
            <div className="divide-y divide-border rounded-2xl border border-border bg-card px-4">
              <Row label={t("rw_overall")} value={overall} onChange={setOverall} testId="rw-overall" />
              <Row label={t("rw_care")} value={care} onChange={setCare} testId="rw-care" />
              <Row label={t("rw_speed")} value={speed} onChange={setSpeed} testId="rw-speed" />
              <Row label={t("rw_courtesy")} value={courtesy} onChange={setCourtesy} testId="rw-courtesy" />
            </div>

            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder={t("rw_commentPh")} className="mt-4 min-h-[72px] resize-none rounded-2xl" data-testid="rw-comment-input" />

            <Button onClick={submit} disabled={!canSubmit} className="mt-4 w-full rounded-full brand-gradient py-4 text-base font-bold text-white disabled:opacity-50" data-testid="rw-submit-btn">
              {t("rw_submit")}
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
